import type { ExtractPropTypes, PropType, SetupContext } from 'vue';
import { computed, ref } from 'vue';
import useBreakpoints from '@getaccept/lib-shared-new/src/composables/breakpoints.composable';
import type { User } from '@getaccept/lib-shared-new/src/users/types/user';
import { DateHelper } from '@getaccept/lib-shared-new/src/helpers/date.helper';
import { DateTime } from 'luxon';
import { getDescriptionContent } from '@getaccept/editor-lib-new';
import type { Task } from '../types/action-plan';
import type { Participant } from '../../types';
import { TaskStatus } from '../types/enums/task-status';
import { TaskHelper } from '../helpers/task.helper';

export const editTaskProps = {
  taskToEdit: { type: Object as PropType<Task>, required: true },
  participants: {
    type: Array as PropType<Participant[]>,
    default: () => [],
  },
  owner: {
    type: Object as PropType<User | Participant>,
    default: () => ({} as User | Participant),
  },
};

export function useEditTask(
  props: ExtractPropTypes<typeof editTaskProps>,
  emit: SetupContext['emit']
) {
  const task = ref({ ...props.taskToEdit });

  const taskUpdatePayload = computed(() =>
    TaskHelper.getTaskUpdatePayload(task.value, props.taskToEdit, props.owner.id)
  );

  const isCompleted = computed(() => props.taskToEdit.status === TaskStatus.Completed);

  const formattedDescription = computed(() => getDescriptionContent(props.taskToEdit.description));

  const deadline = computed(() =>
    props.taskToEdit.deadline
      ? DateHelper.formattedDate(DateTime.fromMillis(props.taskToEdit.deadline))
      : null
  );

  const { isXSmallScreenAndDown } = useBreakpoints();
  const selectableParticipants = computed(() => [{ ...props.owner }, ...props.participants]);

  const handleClickOutside = (event: MouseEvent) => {
    if ((event.target as HTMLElement).id === task.value.id) {
      return;
    }

    emit('close');
  };

  return {
    task,
    isCompleted,
    formattedDescription,
    isXSmallScreenAndDown,
    selectableParticipants,
    deadline,
    taskUpdatePayload,
    handleClickOutside,
  };
}
